import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Select,
  MenuItem,
  TextField,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import { challengeActions } from '../../actions';
import { AvatarIconWithText } from '../Shared';

const useStyles = makeStyles((theme) => ({
  labelRoot: {
    fontWeight: 600,
    lineHeight: 1,
  },
  characters: {
    margin: theme.spacing(1, 0),
    fontSize: '0.75rem',
    position: 'absolute',
    right: 10,
  },
  error: {
    color: theme.palette.auxiliary.red.main,
    marginLeft: 14,
    marginRight: 14,
    marginTop: 3,
    fontSize: '0.6428571428571428rem',
    fontWeight: 400,
    lineHeight: 1.66,
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: 8,
    border: `1px solid ${theme.palette.neutrals.low.lightest}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 30
  }
}));

export function ChallengeCreateCard({ name, description, goal }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [descriptionChar, setDescription] = useState('');
  const { t } = useTranslation();
  const [goalChar, setGoal] = useState('');
  const { ideaFluxes } = useSelector(state => state.domains);
  const { newChallenge: { ideaStepFlux } } = useSelector(state => state.challenge);
  const { register, errors, getValues } = useForm({
    mode: 'onBlur',
  });

  useEffect(() => {
    if (!!name.length && description.length >= 100 && !!goal.length) {
      dispatch(challengeActions.setChallenge({ name, description, goal }));
    };
  }, [name, description, goal]);

  function handleDescription() {
    const description = getValues().description;
    setDescription(description);
  }

  function handleGoal() {
    const goal = getValues().goal;
    setGoal(goal);
  }

  function setPropChallenge({ target: { value, name } }) {
    if (name === 'ideaStepFlux') {
      const selectedItem = ideaFluxes.find(item => item._id === value);
      dispatch(challengeActions.setPropChallenge({ value: selectedItem, name }));
      return;
    }

    dispatch(challengeActions.setPropChallenge({ name, value }));
  }

  const SelectProps = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  };

  return (
    <Card elevation={0} className={classes.card}>
      <AvatarIconWithText
        icon={<DescriptionIcon color="primary" />}
        text={t('desafio')}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            name="name"
            id="name"
            label={t('nome_do_desafio')}
            autoComplete="name"
            type="text"
            variant="filled"
            margin="normal"
            fullWidth
            inputRef={register({
              required: t('titulo_do_desafio_e_obrigatorio'),
            })}
            error={!!errors.name}
            helperText={errors.name && errors.name.message}
            InputLabelProps={{
              classes: {
                root: classes.labelRoot,
              },
            }}
            defaultValue={name}
            onBlur={setPropChallenge}
          />
        </Grid>
        <Grid item xs={12}>
          <Box position="relative">
            <div className={classes.characters}>
              <span>{descriptionChar.length}/10000 caracteres</span>
            </div>
            <TextField
              error={!!errors.description}
              id="outlined-multiline-static"
              name="description"
              label={t('descricao_do_desafio')}
              multiline
              fullWidth
              minRows={8}
              variant="filled"
              onChange={handleDescription}
              inputRef={register({
                maxLength: 10000,
                minLength: 100,
                required: t('descricao_do_desafio_e_obrigatorio'),
              })}
              InputLabelProps={{
                classes: {
                  root: classes.labelRoot,
                },
              }}
              defaultValue={description}
              onBlur={setPropChallenge}
            />
            <p className={classes.error}>
              {errors.description && errors.description.message}
              {errors.description &&
                errors.description.type === 'maxLength' &&
                'Você passou o limite de caracteres'}
              {errors.description &&
                errors.description.type === 'minLength' &&
                t('a_quantidade_minima_de_caracteres_da_descricao_e') +
                ' 100'}
            </p>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box position="relative">
            <div className={classes.characters}>
              <span>{goalChar.length}/10000 caracteres</span>
            </div>
            <TextField
              error={!!errors.goal}
              id="outlined-multiline-static"
              name="goal"
              label={t('objetivos_do_desafio')}
              multiline
              fullWidth
              minRows={8}
              variant="filled"
              onChange={handleGoal}
              inputRef={register({
                maxLength: 10000,
                required: t('objetivos_do_desafio_e_obrigatorio'),
              })}
              InputLabelProps={{
                classes: {
                  root: classes.labelRoot,
                },
              }}
              defaultValue={goal}
              onBlur={setPropChallenge}
            />
            <p className={classes.error}>
              {errors.goal && errors.goal.message}
              {errors.goal &&
                errors.goal.type === 'maxLength' &&
                t('voce_passou_o_limite_de_caracteres')}
            </p>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth variant="filled">
            <InputLabel>{t('selecione_o_fluxo')}</InputLabel>
            <Select
              name="ideaStepFlux"
              MenuProps={SelectProps}
              value={ideaStepFlux?._id}
              onChange={setPropChallenge}
            >
              {ideaFluxes && ideaFluxes.map(item => (
                <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>     
        </Grid>
      </Grid>
    </Card>
  );
}